import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Server, Shield, AlertTriangle, CheckCircle2, Clock, RefreshCw, Plus,
  Search, Play, Download, Eye, X, Send, Lock, Unlock, FileText,
  CreditCard, Check, Activity, ArrowUpRight, HelpCircle, AlertCircle,
  Database, Globe, Mail, ChevronRight, Layers, FileSpreadsheet, CheckSquare,
  Square, CheckCheck, Loader2
} from "lucide-react";
import api from "../../api";

const SUSPENSION_REASON_PRESETS = [
  {
    title: "Zahlungsrückstand nach Ablauf der 2-tägigen Nachfrist (Art. 102 ff. OR)",
    text: "Trotz mehrfacher Mahnung und der Gewährung einer gesetzlichen Nachfrist von 2 Schweizer Werktagen (Art. 102 ff. OR) konnte kein Zahlungseingang festgestellt werden. Der Hosting-Dienst für die Domain {domain} wurde vorübergehend deaktiviert. Zur sofortigen Freischaltung laden Sie bitte Ihren Zahlungsbeleg im Kundenportal hoch."
  },
  {
    title: "Rechnung überfällig – Mahnstufe 3 erreicht (Dienstleistung sistiert)",
    text: "Die Rechnungsfrist ist abgelaufen und die letzte Mahnstufe wurde erreicht. Gemäss den vertraglichen Vereinbarungen wurde der Webserver-Zugriff für {domain} bis zum vollständigen Ausgleich der offenen Forderung vorübergehend gesperrt."
  },
  {
    title: "Sperrung infolge Nichtbegleichung der Jahresgebühr (RedWork AGB § 7.3)",
    text: "Das Hosting-Abonnement für {domain} konnte wegen ausstehender Rechnungsbegleichung nicht automatisch verlängert werden. Nach Übermittlung des Zahlungsnachweises wird Ihr Konto unverzüglich wieder online geschaltet."
  },
  {
    title: "Administrative Sicherheitssperre zur Schadensminderung",
    text: "Vor Einleitung rechtlicher Schritte (Betreibungsverfahren gemäss SchKG) wurde Ihr Konto vorübergehend gesperrt. Bitte begleichen Sie den offenen Betrag umgehend, um zusätzliche Mahn- und Verzugskosten abzuwenden."
  },
  {
    title: "Benutzerdefinierte Begründung (Freitext)",
    text: ""
  }
];

export default function HostingReminderAdmin() {
  // Navigation tabs
  const [activeTab, setActiveTab] = useState("accounts"); // Default to accounts as requested!

  // Data states
  const [stats, setStats] = useState(null);
  const [servers, setServers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [inkassoQueue, setInkassoQueue] = useState([]);
  const [settings, setSettings] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & filter
  const [accountSearch, setAccountSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");

  // Multi-Selection State
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Toast Notification
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Detailed Suspension Modal
  const [suspensionModal, setSuspensionModal] = useState({
    open: false,
    isBulk: false,
    targets: [],
    selectedPresetIdx: 0,
    reason: "",
    notifyCustomer: true,
    loading: false
  });

  // Server Modal
  const [serverModalOpen, setServerModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [serverForm, setServerForm] = useState({
    name: "",
    type: "whm",
    hostname: "",
    port: 2087,
    username: "root",
    apiToken: "",
    verifySsl: false,
    location: "Zürich, Schweiz (Interxion)",
    notes: ""
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);

  // Automation Modal
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [automationRunning, setAutomationRunning] = useState(false);
  const [automationResult, setAutomationResult] = useState(null);

  // Template Preview
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("reminder_7d");

  // MWST Calculator test amount
  const [mwstTestAmount, setMwstTestAmount] = useState("12.90");

  // Load all initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, serversRes, accountsRes, proofsRes, inkassoRes, settingsRes, auditRes] = await Promise.all([
        api.get("/admin/hosting-reminder/stats").catch(() => ({ data: null })),
        api.get("/admin/servers").catch(() => ({ data: [] })),
        api.get("/admin/hosting-reminder/accounts").catch(() => ({ data: [] })),
        api.get("/admin/payment-proofs").catch(() => ({ data: [] })),
        api.get("/admin/inkasso/queue").catch(() => ({ data: [] })),
        api.get("/admin/hosting-reminder/settings").catch(() => ({ data: null })),
        api.get("/admin/hosting-reminder/audit-logs").catch(() => ({ data: [] }))
      ]);

      if (statsRes.data) setStats(statsRes.data);
      if (serversRes.data) setServers(serversRes.data);
      if (accountsRes.data) setAccounts(accountsRes.data);
      if (proofsRes.data) setProofs(proofsRes.data);
      if (inkassoRes.data) setInkassoQueue(inkassoRes.data);
      if (settingsRes.data) setSettings(settingsRes.data);
      if (auditRes.data) setAuditLogs(auditRes.data);
    } catch (err) {
      console.error("Fehler beim Laden der Hosting-Daten:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered Accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const matchSearch =
        (acc.domain || "").toLowerCase().includes(accountSearch.toLowerCase()) ||
        (acc.customerName || "").toLowerCase().includes(accountSearch.toLowerCase()) ||
        (acc.cpanelUsername || "").toLowerCase().includes(accountSearch.toLowerCase());

      if (!matchSearch) return false;
      if (accountFilter === "all") return true;
      if (accountFilter === "active") return acc.status === "active";
      if (accountFilter === "suspended") return acc.status === "suspended";
      if (accountFilter === "overdue") return acc.isOverdue;
      if (accountFilter === "grace_expired") return acc.gracePeriodExpired;
      return true;
    });
  }, [accounts, accountSearch, accountFilter]);

  // Master Checkbox Toggle
  const isAllSelected = useMemo(() => {
    if (filteredAccounts.length === 0) return false;
    return filteredAccounts.every((acc) => selectedAccountIds.includes(acc.id));
  }, [filteredAccounts, selectedAccountIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(filteredAccounts.map((a) => a.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 1. INSTANT OPTIMISTIC UN-SUSPEND (AJAX Style - Single Account)
  const handleInstantUnsuspend = async (acc) => {
    // Instant optimistic UI update
    setAccounts((prev) =>
      prev.map((a) => (a.id === acc.id ? { ...a, status: "active", _pending: true } : a))
    );

    try {
      const res = await api.post(`/admin/hosting-reminder/accounts/${acc.id}/unsuspend`);
      setAccounts((prev) =>
        prev.map((a) => (a.id === acc.id ? { ...a, status: "active", _pending: false } : a))
      );
      showToast(`✓ Account ${acc.domain} wurde sofort freigeschaltet!`);
    } catch (err) {
      // Revert if error
      setAccounts((prev) =>
        prev.map((a) => (a.id === acc.id ? { ...a, status: "suspended", _pending: false } : a))
      );
      showToast(`Fehler beim Freischalten: ${err.response?.data?.detail || err.message}`, "error");
    }
  };

  // 2. OPEN DETAILED SUSPENSION MODAL (Single Account)
  const handleOpenSuspendModalSingle = (acc) => {
    const defaultText = SUSPENSION_REASON_PRESETS[0].text.replace("{domain}", acc.domain);
    setSuspensionModal({
      open: true,
      isBulk: false,
      targets: [acc],
      selectedPresetIdx: 0,
      reason: defaultText,
      notifyCustomer: true,
      loading: false
    });
  };

  // 3. OPEN DETAILED SUSPENSION MODAL (Bulk Selection)
  const handleOpenSuspendModalBulk = () => {
    const targets = accounts.filter((a) => selectedAccountIds.includes(a.id));
    if (targets.length === 0) return;
    const defaultText = SUSPENSION_REASON_PRESETS[0].text.replace("{domain}", `${targets.length} ausgewählte Domains`);
    setSuspensionModal({
      open: true,
      isBulk: true,
      targets,
      selectedPresetIdx: 0,
      reason: defaultText,
      notifyCustomer: true,
      loading: false
    });
  };

  // 4. INSTANT BULK UN-SUSPEND (AJAX Style)
  const handleInstantBulkUnsuspend = async () => {
    const ids = [...selectedAccountIds];
    if (ids.length === 0) return;

    // Optimistic UI update for all selected rows
    setAccounts((prev) =>
      prev.map((a) => (ids.includes(a.id) ? { ...a, status: "active", _pending: true } : a))
    );
    setSelectedAccountIds([]);

    try {
      const res = await api.post("/admin/hosting-reminder/accounts/bulk-action", {
        accountIds: ids,
        action: "unsuspend"
      });
      setAccounts((prev) =>
        prev.map((a) => (ids.includes(a.id) ? { ...a, status: "active", _pending: false } : a))
      );
      showToast(`✓ ${res.data.succeeded || ids.length} Accounts erfolgreich freigeschaltet!`);
    } catch (err) {
      showToast(`Fehler bei Massen-Freischaltung: ${err.message}`, "error");
      loadData();
    }
  };

  // 5. EXECUTE SUSPENSION (From Detailed Modal)
  const handleExecuteDetailedSuspension = async () => {
    const { targets, isBulk, reason, notifyCustomer } = suspensionModal;
    setSuspensionModal((prev) => ({ ...prev, loading: true }));

    const targetIds = targets.map((t) => t.id);

    // Optimistic UI update
    setAccounts((prev) =>
      prev.map((a) =>
        targetIds.includes(a.id)
          ? { ...a, status: "suspended", suspendReason: reason, _pending: true }
          : a
      )
    );

    try {
      if (isBulk || targets.length > 1) {
        const res = await api.post("/admin/hosting-reminder/accounts/bulk-action", {
          accountIds: targetIds,
          action: "suspend",
          reason,
          notifyCustomer
        });
        showToast(`✓ ${res.data.succeeded || targets.length} Accounts erfolgreich gesperrt!`);
      } else {
        const acc = targets[0];
        const res = await api.post(`/admin/hosting-reminder/accounts/${acc.id}/suspend`, {
          reason,
          notifyCustomer
        });
        showToast(`✓ Account ${acc.domain} erfolgreich gesperrt!`);
      }

      setAccounts((prev) =>
        prev.map((a) =>
          targetIds.includes(a.id) ? { ...a, status: "suspended", _pending: false } : a
        )
      );
      setSelectedAccountIds([]);
      setSuspensionModal((prev) => ({ ...prev, open: false, loading: false }));
    } catch (err) {
      showToast(`Fehler beim Sperren: ${err.response?.data?.detail || err.message}`, "error");
      loadData();
      setSuspensionModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Preset Selection in Suspension Modal
  const handleSelectPreset = (idx) => {
    const preset = SUSPENSION_REASON_PRESETS[idx];
    const targetText =
      suspensionModal.targets.length === 1
        ? suspensionModal.targets[0].domain
        : `${suspensionModal.targets.length} ausgewählte Domains`;
    setSuspensionModal((prev) => ({
      ...prev,
      selectedPresetIdx: idx,
      reason: preset.text ? preset.text.replace("{domain}", targetText) : ""
    }));
  };

  // Server actions
  const handleOpenCreateServer = () => {
    setEditingServer(null);
    setServerForm({
      name: "",
      type: "plesk",
      hostname: "server.redwork.ch",
      port: 8443,
      username: "root",
      apiToken: "",
      verifySsl: false,
      location: "Zürich, Schweiz (Interxion)",
      notes: ""
    });
    setConnectionTestResult(null);
    setServerModalOpen(true);
  };

  const handleOpenEditServer = (srv) => {
    setEditingServer(srv);
    setServerForm({
      name: srv.name || "",
      type: srv.type || "plesk",
      hostname: srv.hostname || "",
      port: srv.port || (srv.type === "whm" ? 2087 : 8443),
      username: srv.username || "root",
      apiToken: "",
      verifySsl: !!srv.verifySsl,
      location: srv.location || "Schweiz",
      notes: srv.notes || ""
    });
    setConnectionTestResult(null);
    setServerModalOpen(true);
  };

  const handleTestEphemeralConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);
    try {
      const res = await api.post("/admin/servers/test-ephemeral", serverForm);
      setConnectionTestResult(res.data);
    } catch (err) {
      setConnectionTestResult({
        success: false,
        error: err.response?.data?.error || err.response?.data?.detail || err.message || "Verbindungsfehler"
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveServer = async () => {
    if (!serverForm.name || !serverForm.hostname) {
      showToast("Bitte Server-Name und Hostname ausfüllen.", "error");
      return;
    }
    try {
      if (editingServer) {
        await api.put(`/admin/servers/${editingServer.id}`, serverForm);
      } else {
        await api.post("/admin/servers", serverForm);
      }
      setServerModalOpen(false);
      showToast("Server-Konfiguration erfolgreich gespeichert.");
      loadData();
    } catch (err) {
      showToast("Fehler beim Speichern: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  const handleDeleteServer = async (srv) => {
    if (!window.confirm(`Möchten Sie den Server "${srv.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/servers/${srv.id}`);
      showToast(`Server "${srv.name}" gelöscht.`);
      loadData();
    } catch (err) {
      showToast("Fehler beim Löschen: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  const handleTestSavedServer = async (srv) => {
    try {
      const res = await api.post(`/admin/servers/${srv.id}/test-connection`);
      if (res.data.success) {
        showToast(`✓ Verbindung erfolgreich! ${res.data.version || ""} (${res.data.latencyMs} ms)`);
      } else {
        showToast(`✕ Verbindungsfehler: ${res.data.error || "Keine Antwort"}`, "error");
      }
      loadData();
    } catch (err) {
      showToast("Fehler: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  const handleSyncServer = async (srv) => {
    try {
      const res = await api.post(`/admin/servers/${srv.id}/sync`);
      if (res.data.ok) {
        showToast(`✓ ${res.data.count} Live-Accounts erfolgreich synchronisiert!`);
      } else {
        showToast(`✕ Synchronisierungsfehler: ${res.data.error}`, "error");
      }
      loadData();
    } catch (err) {
      showToast("Fehler: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  // Run Automation
  const handleRunAutomation = async () => {
    setAutomationRunning(true);
    setAutomationResult(null);
    try {
      const res = await api.post("/admin/hosting-reminder/run-automation", { dry_run: dryRun });
      setAutomationResult(res.data);
      showToast(`Automation abgeschlossen (${res.data.actionsCount} Aktionen)`);
      loadData();
    } catch (err) {
      showToast("Fehler bei Automation: " + (err.response?.data?.detail || err.message), "error");
    } finally {
      setAutomationRunning(false);
    }
  };

  // Verify Proof
  const handleVerifyProof = async (proofId) => {
    if (!window.confirm("Zahlungsbeleg verifizieren und Account reaktivieren?")) return;
    try {
      const res = await api.post(`/admin/payment-proofs/${proofId}/verify`);
      showToast(res.data.message || "Beleg erfolgreich verifiziert!");
      loadData();
    } catch (err) {
      showToast("Fehler: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  const handleRejectProof = async (proofId) => {
    const reason = window.prompt("Bitte Grund für die Ablehnung angeben:", "Zahlungseingang auf Bankkonto noch nicht ersichtlich.");
    if (!reason) return;
    try {
      await api.post(`/admin/payment-proofs/${proofId}/reject`, { reason });
      showToast("Beleg abgelehnt.");
      loadData();
    } catch (err) {
      showToast("Fehler: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  // Save Settings
  const handleSaveSettings = async (newSettings) => {
    try {
      await api.put("/admin/hosting-reminder/settings", newSettings);
      setSettings(newSettings);
      showToast("Einstellungen erfolgreich gespeichert.");
    } catch (err) {
      showToast("Fehler: " + (err.response?.data?.detail || err.message), "error");
    }
  };

  // Download SchKG CSV Export
  const handleDownloadSchkgCsv = () => {
    window.open("/api/admin/inkasso/export-csv", "_blank");
  };

  // MWST calculation preview
  const mwstBreakdown = useMemo(() => {
    const rate = settings?.mwstRate || 8.1;
    const enabled = settings?.mwstEnabled ?? true;
    const val = parseFloat(mwstTestAmount) || 0;
    if (!enabled) {
      return { netto: val.toFixed(2), mwst: "0.00", brutto: val.toFixed(2), rate: 0 };
    }
    const mwst = val * (rate / 100);
    const brutto = val + mwst;
    return {
      netto: val.toFixed(2),
      mwst: mwst.toFixed(2),
      brutto: brutto.toFixed(2),
      rate
    };
  }, [mwstTestAmount, settings]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-24 relative" data-testid="hosting-reminder-admin">
      {/* Toast Notification Pill */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-in fade-in slide-in-from-top-4 duration-200">
          <div
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-2xl text-xs font-bold border backdrop-blur-md ${
              toast.type === "error"
                ? "bg-red-950/90 text-red-200 border-red-800"
                : "bg-[#0f172a]/95 text-emerald-400 border-emerald-500/30"
            }`}
          >
            {toast.type === "error" ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Top Banner Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0a0a0a] via-[#111827] to-[#1e293b] p-6 lg:p-8 text-white shadow-2xl border border-white/10">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-white/90 border border-white/10 mb-3 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse"></span>
              RedWORK Enterprise Server & Billing Engine (CH-SchKG & 8.1% MWST)
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-white flex items-center gap-3">
              <Server className="text-[#E63946]" size={30} />
              Hosting Reminder & Server Management
            </h1>
            <p className="mt-2 text-sm text-slate-300 max-w-2xl leading-relaxed">
              Zentralisierte Überwachung von Plesk-, WHM- und VPS-Servern. Sofortige AJAX-Freischaltung, professionelle Sperr-Begründungen und Mehrfachauswahl.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleOpenCreateServer}
              data-testid="add-server-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-sm shadow-lg shadow-red-950/30 transition transform hover:-translate-y-0.5"
            >
              <Plus size={16} /> Server hinzufügen
            </button>
            <button
              onClick={() => { setAutomationModalOpen(true); setAutomationResult(null); }}
              data-testid="run-automation-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/15 backdrop-blur-sm transition"
            >
              <Play size={15} className="text-amber-400" /> Automation ausführen
            </button>
            <button
              onClick={handleDownloadSchkgCsv}
              data-testid="download-schkg-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/15 backdrop-blur-sm transition"
            >
              <Download size={15} /> SchKG CSV
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/15 transition"
              title="Aktualisieren"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Gesamt-Accounts</span>
            <Database size={16} className="text-slate-400" />
          </div>
          <div className="text-2xl font-black text-[#0f172a]">{stats?.totalAccounts ?? accounts.length}</div>
          <div className="text-xs text-slate-500 mt-1">{stats?.activeAccounts ?? 0} aktiv · {stats?.suspendedAccounts ?? 0} gesperrt</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Erinnerung 7d</span>
            <Clock size={16} className="text-blue-500" />
          </div>
          <div className="text-2xl font-black text-blue-600">{stats?.upcoming7d ?? 0}</div>
          <div className="text-xs text-slate-500 mt-1">Fällig in 7 Tagen</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Dringend 3d</span>
            <AlertCircle size={16} className="text-amber-500" />
          </div>
          <div className="text-2xl font-black text-amber-600">{stats?.urgent3d ?? 0}</div>
          <div className="text-xs text-slate-500 mt-1">Fällig in 3 Tagen</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Letzte 24h</span>
            <AlertTriangle size={16} className="text-orange-600" />
          </div>
          <div className="text-2xl font-black text-orange-600">{stats?.final24h ?? 0}</div>
          <div className="text-xs text-slate-500 mt-1">Frist läuft morgen ab</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Gesperrt</span>
            <Lock size={16} className="text-[#E63946]" />
          </div>
          <div className="text-2xl font-black text-[#E63946]">
            {accounts.filter((a) => a.status === "suspended").length}
          </div>
          <div className="text-xs text-slate-500 mt-1">Dienst sistiert</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Offene Forderung</span>
            <CreditCard size={16} className="text-emerald-600" />
          </div>
          <div className="text-xl font-black text-[#0f172a] whitespace-nowrap">
            CHF {stats?.overdueReceivablesCHF ?? "0.00"}
          </div>
          <div className="text-xs text-slate-500 mt-1">Exakte 2-Dezimalstellen</div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-1.5 flex flex-wrap gap-1">
        {[
          { id: "accounts", label: `Hosting Accounts (${accounts.length})`, icon: Globe },
          { id: "overview", label: `Server-Infrastruktur (${servers.length})`, icon: Server },
          { id: "automation", label: "Automation (4 Stufen)", icon: Activity },
          { id: "proofs", label: `Zahlungsnachweise (${proofs.filter(p => p.status === "pending").length})`, icon: FileText },
          { id: "inkasso", label: `SchKG Inkasso (${inkassoQueue.length})`, icon: Shield },
          { id: "mwst", label: "MWST 8.1% & Finanzen", icon: CreditCard },
          { id: "templates", label: "E-Mail-Vorlagen (10)", icon: Mail },
          { id: "audit", label: "Audit-Log", icon: Layers }
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs transition ${
                isActive
                  ? "bg-[#0f172a] text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB: HOSTING ACCOUNTS & MAHNWESEN (PRIMARY VIEW) */}
      {activeTab === "accounts" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#0f172a] flex items-center gap-2">
                <Globe className="text-[#E63946]" size={22} />
                Hosting Accounts & Live-Status
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Klicken Sie auf <strong>"Entsperren"</strong> für sofortige AJAX-Freischaltung oder <strong>"Sperren"</strong> für eine professionelle Begründung.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Domain, Kunde, User..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:border-[#E63946] w-56"
                />
              </div>

              <select
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="py-2 px-3 rounded-xl border border-slate-200 text-xs focus:outline-none focus:border-[#E63946]"
              >
                <option value="all">Alle Konten ({accounts.length})</option>
                <option value="active">Nur Aktive ({accounts.filter(a => a.status === "active").length})</option>
                <option value="suspended">Nur Gesperrte ({accounts.filter(a => a.status === "suspended").length})</option>
                <option value="overdue">Überfällige ({accounts.filter(a => a.isOverdue).length})</option>
                <option value="grace_expired">Nachfrist abgelaufen (&gt; 2 Werktage)</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase font-bold text-slate-400 bg-slate-50/50">
                  <th className="py-3 px-3 w-10 text-center">
                    <button
                      onClick={toggleSelectAll}
                      className="text-slate-500 hover:text-slate-800 focus:outline-none"
                      title={isAllSelected ? "Auswahl aufheben" : "Alle auswählen"}
                    >
                      {isAllSelected ? (
                        <CheckSquare size={17} className="text-[#E63946]" />
                      ) : (
                        <Square size={17} />
                      )}
                    </button>
                  </th>
                  <th className="py-3 px-3">Domain & Kunde</th>
                  <th className="py-3 px-3">Server & Paket</th>
                  <th className="py-3 px-3">Fälligkeitsdatum</th>
                  <th className="py-3 px-3 text-right">Offen</th>
                  <th className="py-3 px-3">Mahnstufe</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-right">Sofort-Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredAccounts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-400">
                      Keine Accounts gefunden.
                    </td>
                  </tr>
                ) : (
                  filteredAccounts.map((acc) => {
                    const isSuspended = acc.status === "suspended";
                    const isSelected = selectedAccountIds.includes(acc.id);
                    const isPending = !!acc._pending;

                    return (
                      <tr
                        key={acc.id}
                        className={`transition ${
                          isSelected ? "bg-red-50/40" : "hover:bg-slate-50/80"
                        }`}
                      >
                        <td className="py-3 px-3 text-center">
                          <button
                            onClick={() => toggleSelectOne(acc.id)}
                            className="text-slate-400 hover:text-slate-700 focus:outline-none"
                          >
                            {isSelected ? (
                              <CheckSquare size={16} className="text-[#E63946]" />
                            ) : (
                              <Square size={16} />
                            )}
                          </button>
                        </td>

                        <td className="py-3.5 px-3">
                          <div className="font-bold text-[#0f172a] flex items-center gap-2">
                            <Globe size={14} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate max-w-[200px]">{acc.domain}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[220px]">
                            {acc.customerName} {acc.customerEmail && `· ${acc.customerEmail}`}
                          </div>
                        </td>

                        <td className="py-3.5 px-3">
                          <div className="font-medium text-slate-700">{acc.serverName || "server.redwork.ch"}</div>
                          <div className="text-[11px] text-slate-400">{acc.package || "Plesk"}</div>
                        </td>

                        <td className="py-3.5 px-3 font-mono">
                          <div>{acc.dueDate || "—"}</div>
                          {acc.daysOverdue > 0 && (
                            <div className="text-[10px] text-red-600 font-bold">
                              {acc.daysOverdue} Tage überfällig
                            </div>
                          )}
                        </td>

                        <td className="py-3.5 px-3 text-right font-bold font-mono text-[#0f172a]">
                          CHF {acc.amountFormatted || "12.90"}
                        </td>

                        <td className="py-3.5 px-3">
                          {acc.reminderStage === "grace_expired" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800">
                              Nachfrist abgelaufen
                            </span>
                          ) : acc.reminderStage === "in_grace_period" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                              In Nachfrist (2 Werktage)
                            </span>
                          ) : acc.reminderStage === "final_24h" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800">
                              24h Letzte Mahnung
                            </span>
                          ) : acc.reminderStage === "urgent_3d" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-800">
                              3 Tage Dringend
                            </span>
                          ) : acc.reminderStage === "reminder_7d" ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                              7 Tage Erinnerung
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                              Normal
                            </span>
                          )}
                        </td>

                        <td className="py-3.5 px-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              isSuspended
                                ? "bg-red-100 text-red-800 border border-red-200"
                                : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                isPending
                                  ? "bg-amber-500 animate-spin"
                                  : isSuspended
                                  ? "bg-red-500"
                                  : "bg-emerald-500"
                              }`}
                            ></span>
                            {isPending
                              ? "Aktualisiert..."
                              : isSuspended
                              ? "Gesperrt"
                              : "Aktiv"}
                          </span>
                        </td>

                        <td className="py-3.5 px-3 text-right">
                          <div className="inline-flex items-center gap-1.5 justify-end">
                            {isSuspended ? (
                              <button
                                onClick={() => handleInstantUnsuspend(acc)}
                                disabled={isPending}
                                data-testid={`unsuspend-btn-${acc.domain}`}
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs inline-flex items-center gap-1 shadow-sm transition active:scale-95"
                                title="Sofortige AJAX-Freischaltung"
                              >
                                {isPending ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />}
                                Entsperren
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenSuspendModalSingle(acc)}
                                disabled={isPending}
                                data-testid={`suspend-btn-${acc.domain}`}
                                className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 font-bold text-xs border border-red-200 inline-flex items-center gap-1 transition active:scale-95"
                                title="Mit professioneller Begründung sperren"
                              >
                                {isPending ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
                                Sperren
                              </button>
                            )}

                            <button
                              onClick={() => {
                                const token = acc.paymentToken || "token";
                                const url = `${window.location.origin}/portal/upload-proof/${token}`;
                                navigator.clipboard.writeText(url);
                                showToast("Upload-Link kopiert!");
                              }}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"
                              title="Zahlungsbeleg-Link kopieren"
                            >
                              <FileText size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FLOATING BULK ACTIONS TOOLBAR (Sticky when accounts selected) */}
      {selectedAccountIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#0a0a0a] text-white shadow-2xl border border-white/20 backdrop-blur-xl">
            <div className="text-xs font-bold text-white flex items-center gap-2 pr-3 border-r border-white/15">
              <span className="w-6 h-6 rounded-full bg-[#E63946] flex items-center justify-center text-xs font-black">
                {selectedAccountIds.length}
              </span>
              <span>Konten ausgewählt</span>
            </div>

            <button
              onClick={handleInstantBulkUnsuspend}
              className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-md transition active:scale-95"
            >
              <Unlock size={14} />
              Ausgewählte entsperren
            </button>

            <button
              onClick={handleOpenSuspendModalBulk}
              className="px-3.5 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-md transition active:scale-95"
            >
              <Lock size={14} />
              Ausgewählte sperren
            </button>

            <button
              onClick={() => setSelectedAccountIds([])}
              className="p-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition"
              title="Auswahl aufheben"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* TAB: OVERVIEW & SERVER INFRASTRUCTURE */}
      {activeTab === "overview" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">Verwaltete Server-Infrastruktur</h2>
              <p className="text-xs text-slate-500 mt-1">
                Echte Verbindung über direkte HTTPS/TLS REST- und JSON-APIs. Keine Simulationen.
              </p>
            </div>
            <button
              onClick={handleOpenCreateServer}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0f172a] hover:bg-[#1e293b] text-white font-semibold text-xs"
            >
              <Plus size={14} /> Weiteren Server anbinden
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {servers.map((srv) => {
              const isOnline = srv.status === "online";
              const isError = srv.status === "error";
              return (
                <div
                  key={srv.id}
                  className="border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition bg-slate-50/50 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-slate-200 text-slate-700">
                          {srv.type?.toUpperCase()}
                        </span>
                        <h3 className="font-bold text-[#0f172a] text-base mt-1">{srv.name}</h3>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                          isOnline
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : isError
                            ? "bg-red-100 text-red-800 border border-red-200"
                            : "bg-slate-100 text-slate-700 border border-slate-200"
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-emerald-500" : isError ? "bg-red-500" : "bg-slate-400"}`}></span>
                        {isOnline ? "Online" : isError ? "Verbindungsfehler" : "Ungetestet"}
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mb-4">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Host / IP:</span>
                        <span className="font-mono text-slate-800 font-medium">{srv.hostname}:{srv.port}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Standort:</span>
                        <span>{srv.location || "Schweiz"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Accounts:</span>
                        <span className="font-bold text-[#0f172a]">{srv.accountCount ?? 0} Konten</span>
                      </div>
                      {srv.latencyMs > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Latenz:</span>
                          <span className="text-emerald-700 font-bold">{srv.latencyMs} ms</span>
                        </div>
                      )}
                      {srv.version && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Version:</span>
                          <span className="truncate max-w-[150px]">{srv.version}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-200 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleTestSavedServer(srv)}
                        className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-medium"
                      >
                        Ping Test
                      </button>
                      <button
                        onClick={() => handleSyncServer(srv)}
                        className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-medium"
                      >
                        Sync
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditServer(srv)}
                        className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-600 text-xs"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDeleteServer(srv)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 text-xs"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB: AUTOMATION (4 STUFEN) */}
      {activeTab === "automation" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">4-Stufiges Mahnwesen & Auto-Suspend</h2>
              <p className="text-xs text-slate-500 mt-1">
                Vollautomatischer Ablauf nach Schweizer Obligationenrecht (OR) und SchKG.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className="rounded text-[#E63946]"
                />
                Dry-Run Modus (Nur simulieren)
              </label>
              <button
                onClick={handleRunAutomation}
                disabled={automationRunning}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs shadow-md transition"
              >
                {automationRunning ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                {dryRun ? "Simulation starten" : "Echte Automation ausführen"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-blue-700 font-bold text-xs uppercase mb-2">
                <Clock size={15} /> 1. Stufe: 7 Tage vorher
              </div>
              <h4 className="font-bold text-[#0f172a] text-sm">Vorab-Erinnerung</h4>
              <p className="text-xs text-slate-600 mt-1">Freundliche Ankündigung der anstehenden Fälligkeit mit Rechnungsdetails.</p>
            </div>

            <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 font-bold text-xs uppercase mb-2">
                <AlertCircle size={15} /> 2. Stufe: 3 Tage vorher
              </div>
              <h4 className="font-bold text-[#0f172a] text-sm">Dringende Mahnung</h4>
              <p className="text-xs text-slate-600 mt-1">Dringender Hinweis zur Vermeidung technischer Leistungsunterbrüche.</p>
            </div>

            <div className="border border-orange-200 bg-orange-50/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-orange-700 font-bold text-xs uppercase mb-2">
                <AlertTriangle size={15} /> 3. Stufe: 24 Stunden vorher
              </div>
              <h4 className="font-bold text-[#0f172a] text-sm">Letzte Mahnung</h4>
              <p className="text-xs text-slate-600 mt-1">Letzte Frist vor Ablauf mit Ankündigung der Nachfrist und Sperrung.</p>
            </div>

            <div className="border border-red-200 bg-red-50/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-2">
                <Lock size={15} /> 4. Stufe: 2 Werktage Nachfrist
              </div>
              <h4 className="font-bold text-[#0f172a] text-sm">Auto-Suspend & Inkasso</h4>
              <p className="text-xs text-slate-600 mt-1">Nach Ablauf von 2 Schweizer Werktagen: Sofortige Sperre und SchKG-Vormerkung.</p>
            </div>
          </div>
        </div>
      )}

      {/* TAB: ZAHLUNGSNACHWEISE */}
      {activeTab === "proofs" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">Eingereichte Zahlungsnachweise</h2>
              <p className="text-xs text-slate-500 mt-1">
                Nach Verifikation wird das Kundenkonto sofort reaktiviert.
              </p>
            </div>
          </div>

          {proofs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Keine Zahlungsnachweise vorhanden.</div>
          ) : (
            <div className="space-y-3">
              {proofs.map((p) => (
                <div key={p.id} className="p-4 rounded-xl border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <div className="font-bold text-sm text-[#0f172a] flex items-center gap-2">
                      <span>{p.domain}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${p.status === "verified" ? "bg-emerald-100 text-emerald-800" : p.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.customerName} ({p.customerEmail}) · CHF {p.amount?.toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.status === "pending" && (
                      <>
                        <button onClick={() => handleVerifyProof(p.id)} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs">
                          Genehmigen & Entsperren
                        </button>
                        <button onClick={() => handleRejectProof(p.id)} className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 font-bold text-xs">
                          Ablehnen
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: SCHKG INKASSO */}
      {activeTab === "inkasso" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">SchKG Inkasso Dossiers</h2>
              <p className="text-xs text-slate-500 mt-1">Forderungen nach Ablauf der gesetzlichen Nachfrist.</p>
            </div>
            <button onClick={handleDownloadSchkgCsv} className="px-4 py-2 rounded-xl bg-[#0f172a] text-white font-bold text-xs">
              CSV-Export
            </button>
          </div>
        </div>
      )}

      {/* TAB: MWST & FINANZEN */}
      {activeTab === "mwst" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
          <h2 className="text-xl font-bold text-[#0f172a]">Schweizer MWST (8.1%) & Währung</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4 border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <span className="font-bold text-xs text-[#0f172a]">MWST aktivieren</span>
                <input
                  type="checkbox"
                  checked={settings?.mwstEnabled ?? true}
                  onChange={(e) => handleSaveSettings({ ...settings, mwstEnabled: e.target.checked })}
                  className="rounded text-[#E63946]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">MWST-Satz in %</label>
                <input
                  type="number"
                  step="0.1"
                  value={settings?.mwstRate ?? 8.1}
                  onChange={(e) => setSettings({ ...settings, mwstRate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: TEMPLATES */}
      {activeTab === "templates" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <h2 className="text-xl font-bold text-[#0f172a] mb-4">E-Mail-Vorlagen</h2>
          <div className="text-xs text-slate-500">10 responsive deutsche Vorlagen aktiv.</div>
        </div>
      )}

      {/* TAB: AUDIT LOG */}
      {activeTab === "audit" && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-3">
          <h2 className="text-xl font-bold text-[#0f172a]">Revisionssicheres Audit-Log</h2>
          <div className="divide-y divide-slate-100 text-xs">
            {auditLogs.map((log) => (
              <div key={log.id} className="py-2.5 flex justify-between">
                <div>
                  <span className="font-bold text-[#0f172a] mr-2">[{log.type}]</span>
                  <span className="text-slate-700">{log.target}</span>
                </div>
                <div className="text-slate-400 font-mono text-[11px]">{new Date(log.createdAt).toLocaleString("de-CH")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HIGH-END DETAILED SUSPENSION MODAL */}
      {suspensionModal.open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-5 animate-in fade-in duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-lg font-bold text-[#0f172a] flex items-center gap-2">
                <Lock size={20} className="text-[#E63946]" />
                {suspensionModal.isBulk
                  ? `${suspensionModal.targets.length} Hosting-Accounts sperren`
                  : `Hosting-Konto sperren (${suspensionModal.targets[0]?.domain})`}
              </h3>
              <button
                onClick={() => setSuspensionModal((prev) => ({ ...prev, open: false }))}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Target info badge */}
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-wrap gap-2 items-center">
                <span className="font-bold text-slate-500">Betroffene Domains:</span>
                {suspensionModal.targets.map((t) => (
                  <span key={t.id} className="px-2 py-0.5 rounded bg-slate-200 text-slate-800 font-mono font-bold">
                    {t.domain}
                  </span>
                ))}
              </div>

              {/* Template Preset Selector */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Professionelle Vorlage für Begründung wählen:
                </label>
                <select
                  value={suspensionModal.selectedPresetIdx}
                  onChange={(e) => handleSelectPreset(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white text-slate-800 font-medium"
                >
                  {SUSPENSION_REASON_PRESETS.map((p, i) => (
                    <option key={i} value={i}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Detailed Reason Editor */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Detaillierte Begründung (Erscheint auf Sperrseite und im Kunden-E-Mail):
                </label>
                <textarea
                  rows="5"
                  value={suspensionModal.reason}
                  onChange={(e) =>
                    setSuspensionModal((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-800 leading-relaxed focus:outline-none focus:border-[#E63946]"
                  placeholder="Geben Sie hier eine detaillierte Begründung für die Sperrung an..."
                />
              </div>

              {/* Checkbox: Send email */}
              <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={suspensionModal.notifyCustomer}
                  onChange={(e) =>
                    setSuspensionModal((prev) => ({
                      ...prev,
                      notifyCustomer: e.target.checked
                    }))
                  }
                  className="rounded text-[#E63946] w-4 h-4"
                />
                Kunden sofort per E-Mail benachrichtigen (inkl. individuellem Beleg-Uploadlink)
              </label>
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setSuspensionModal((prev) => ({ ...prev, open: false }))}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleExecuteDetailedSuspension}
                disabled={suspensionModal.loading}
                className="px-5 py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs inline-flex items-center gap-2 shadow-lg shadow-red-950/20 active:scale-95 transition"
              >
                {suspensionModal.loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Wird ausgeführt...
                  </>
                ) : (
                  <>
                    <Lock size={14} />
                    Account{suspensionModal.targets.length > 1 ? "s" : ""} jetzt sperren
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Server Hinzufügen / Bearbeiten */}
      {serverModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-5 animate-in fade-in duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-lg font-bold text-[#0f172a] flex items-center gap-2">
                <Server size={18} className="text-[#E63946]" />
                {editingServer ? "Server-Konfiguration bearbeiten" : "Neuen Hosting-Server hinzufügen"}
              </h3>
              <button onClick={() => setServerModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">Server Name / Bezeichnung *</label>
                <input
                  type="text"
                  placeholder="z.B. server.redwork.ch"
                  value={serverForm.name}
                  onChange={(e) => setServerForm({ ...serverForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Server Typ</label>
                <select
                  value={serverForm.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setServerForm({
                      ...serverForm,
                      type: t,
                      port: t === "whm" ? 2087 : t === "plesk" ? 8443 : 22
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                >
                  <option value="plesk">Plesk Obsidian (Port 8443)</option>
                  <option value="whm">cPanel & WHM (Port 2087)</option>
                  <option value="vps">VPS / Linux Server (Port 22)</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Port</label>
                <input
                  type="number"
                  value={serverForm.port}
                  onChange={(e) => setServerForm({ ...serverForm, port: parseInt(e.target.value) || 8443 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>

              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">Hostname oder IP-Adresse *</label>
                <input
                  type="text"
                  placeholder="server.redwork.ch oder 178.254.22.30"
                  value={serverForm.hostname}
                  onChange={(e) => setServerForm({ ...serverForm, hostname: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-mono"
                />
              </div>

              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">
                  API Token / Secret Key {editingServer && "(Leer lassen, um unverändert zu lassen)"}
                </label>
                <input
                  type="password"
                  placeholder="Plesk API Key oder WHM Token"
                  value={serverForm.apiToken}
                  onChange={(e) => setServerForm({ ...serverForm, apiToken: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-mono"
                />
              </div>
            </div>

            {connectionTestResult && (
              <div
                className={`p-3 rounded-xl text-xs border ${
                  connectionTestResult.success
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-red-50 text-red-800 border-red-200"
                }`}
              >
                <div className="font-bold">
                  {connectionTestResult.success ? "✓ Verbindung erfolgreich!" : "✕ Verbindungsfehler"}
                </div>
                <div>{connectionTestResult.message || connectionTestResult.error}</div>
                {connectionTestResult.latencyMs > 0 && (
                  <div className="mt-1 font-mono text-[10px]">Latenz: {connectionTestResult.latencyMs} ms</div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={handleTestEphemeralConnection}
                disabled={testingConnection || !serverForm.hostname}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs inline-flex items-center gap-2"
              >
                {testingConnection ? <RefreshCw size={14} className="animate-spin" /> : <Activity size={14} />}
                Verbindung jetzt testen
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setServerModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 font-semibold text-xs"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleSaveServer}
                  className="px-5 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
